import { useState, useMemo, useEffect, useRef } from "react";
import { useAnalyticsWrapper, useStoreWrapper } from "../../../store/store";
import {
  ActionBtnContainer,
  ActionBtnU,
  ActionContainer,
  Label,
  Select,
  customStyles,
} from "../../../screens/User/analytics/fall-analysis/form/sharedImports";
import { useFormik } from "formik";
import {
  ALL_INCIDENTS_OPTION,
  getIncidentListInitialValues,
  getIncidentTrendInitialValues,
  getRepeatIncidentInitialValues,
  getStatusReportInitialValues,
  getWoundAnalysisInitialValues,
  getIncidentRateInitialValues,
  getInjuryReportInitialValues,
  mapIncidentTypeOptions,
  resolveIncidentTypeSelection,
  getBehaviorAnalysisInitialValues,
} from "../../../utils/helpers/analyticsUtils";

import useIncident from "../../../hooks/useIncident";
import useSkinIssueTypes, {
  ALL_SKIN_ISSUE_TYPES_OPTION,
} from "../../../hooks/useSkinIssueTypes";
import useInjuryTypes, {
  ALL_INJURY_TYPES_OPTION,
} from "../../../hooks/useInjuryTypes";
import moment from "moment";
import {
  incidentListValidator,
  incidentTrendValidator,
  incidentRateValidator,
  repeatIncidentValidator,
  statusReportValidator,
  woundAnalysisValidator,
  behaviorAnalysisValidator,
  injuryReportValidator,
} from "../../../utils/validators/analyticsValidator";
import useAnalytics from "../../../hooks/useAnalytics";
import { useQueryClient } from "@tanstack/react-query";
import {
  DateRangePicker,
  IncidentTypeSelect,
} from "../../../screens/User/analytics/form/incident-inputs";
import useBehaviorAnalysisOptions, {
  ALL_BEHAVIOR_INCIDENT_TYPES_OPTION,
  ALL_BEHAVIOR_INCIDENT_SUB_TYPES_OPTION,
} from "../../../screens/User/analytics/behaviorAnalysis/useBehaviorAnalysisOptions";

type GeneralFormProps = {
  closeFn: () => void;
  submitFn?: () => void;
  bottom: boolean;
  initialFacility?: any;
  initialStartDate?: string;
  initialEndDate?: string;
  isModifyMode?: boolean; // New prop to control modify mode
};

const GeneralForm = ({
  closeFn,
  submitFn,
  bottom,
  initialFacility = null,
  initialStartDate = "",
  initialEndDate = "",
  isModifyMode = false,
}: GeneralFormProps) => {
  const queryClient = useQueryClient();

  const {
    reportType,
    setTrendSelectedFacility,
    setStartDate,
    setEndDate,
    startDate,
    endDate,
    woundFacility,
    setWoundFacility,
    setAnalyticsIncidentList,
    setAnalyticsIncidentTrend,
    setAnalyticsRepeatIncident,
    setAnalyticsStatusReport,
    setWoundAnalysisIncidents,
    setAnalyticsIncidentRate,
    setAnalyticsInjuryReport,
    setGlobalAnaylyticsFacility,
    globalAnaylyticsFacility,
    setGlobalAnalyticsSelectedIncidentType,
    globalAnalyticsSelectedIncidentType,
    setGlobalAnalyticsSelectedResidentStatus,
    globalAnalyticsSelectedResidentStatus,
    setGlobalAnalyticsSelectedWoundType,
    globalAnalyticsSelectedWoundType,
    setGlobalAnalyticsSelectedInjuryType,
    globalAnalyticsSelectedInjuryType,
    grouping,
    setGrouping,
    behaviorFacility,
    setBehaviorFacility,
    setBehaviorAnalysisIncidents,
    globalAnalyticsSelectedBehaviorIncidentType,
    setGlobalAnalyticsSelectedBehaviorIncidentType,
    globalAnalyticsSelectedBehaviorIncidentSubType,
    setGlobalAnalyticsSelectedBehaviorIncidentSubType,
  } = useAnalyticsWrapper((state) => ({
    reportType: state.reportType,
    setTrendSelectedFacility: state.setTrendSelectedFacility,
    setStartDate: state.setStartDate,
    setEndDate: state.setEndDate,
    startDate: state.startDate,
    endDate: state.endDate,
    woundFacility: state.woundFacility,
    setWoundFacility: state.setWoundFacility,
    setAnalyticsIncidentList: state.setAnalyticsIncidentList,
    setAnalyticsIncidentTrend: state.setAnalyticsIncidentTrend,
    setAnalyticsRepeatIncident: state.setAnalyticsRepeatIncident,
    setAnalyticsStatusReport: state.setAnalyticsStatusReport,
    setWoundAnalysisIncidents: state.setWoundAnalysisIncidents,
    setAnalyticsIncidentRate: state.setAnalyticsIncidentRate,
    setAnalyticsInjuryReport: state.setAnalyticsInjuryReport,
    incidentSelectedFacilities: state.incidentSelectedFacilities,
    setIncidentSelectedFacilities: state.setIncidentSelectedFacilities,
    globalAnaylyticsFacility: state.globalAnaylyticsFacility,
    setGlobalAnaylyticsFacility: state.setGlobalAnaylyticsFacility,
    globalSelectedFacility: state.globalSelectedFacility,
    setGlobalAnalyticsSelectedIncidentType:
      state.setGlobalAnalyticsSelectedIncidentType,
    globalAnalyticsSelectedIncidentType:
      state.globalAnalyticsSelectedIncidentType,
    setGlobalAnalyticsSelectedResidentStatus:
      state.setGlobalAnalyticsSelectedResidentStatus,
    globalAnalyticsSelectedResidentStatus:
      state.globalAnalyticsSelectedResidentStatus,
    setGlobalAnalyticsSelectedInjuryType:
      state.setGlobalAnalyticsSelectedInjuryType,
    globalAnalyticsSelectedInjuryType: state.globalAnalyticsSelectedInjuryType,
    setGlobalAnalyticsSelectedWoundType:
      state.setGlobalAnalyticsSelectedWoundType,
    globalAnalyticsSelectedWoundType: state.globalAnalyticsSelectedWoundType,
    behaviorFacility: state.behaviorFacility,
    setBehaviorFacility: state.setBehaviorFacility,
    setBehaviorAnalysisIncidents: state.setBehaviorAnalysisIncidents,
    globalAnalyticsSelectedBehaviorIncidentType:
      state.globalAnalyticsSelectedBehaviorIncidentType,
    setGlobalAnalyticsSelectedBehaviorIncidentType:
      state.setGlobalAnalyticsSelectedBehaviorIncidentType,
    globalAnalyticsSelectedBehaviorIncidentSubType: state.globalAnalyticsSelectedBehaviorIncidentSubType,
    setGlobalAnalyticsSelectedBehaviorIncidentSubType: state.setGlobalAnalyticsSelectedBehaviorIncidentSubType,
    grouping: state.grouping,
    setGrouping: state.setGrouping,
  }));

  const [facilityOptions, setFacilityOptions] = useState<any>([]);
  const [allFacilitySelected, setAllFacilitySelected] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<any>([]);
  const [incidentListOptions, setIncidentListOptions] = useState<any>([]);
  const [selectedIncidentTypes, setSelectedIncidentTypes] = useState<any>([]);
  const [allIncidentsSelected, setAllIncidentsSelected] = useState(false);
  const [incidentTypes, setIncidentTypes] = useState<string>("");
  const [residentStatus, setResidentStatus] = useState<string[]>([]);
  const [skinIssueTypes, setSkinIssueTypes] = useState<string[]>([]);
  const [selectedSkinIssueTypes, setSelectedSkinIssueTypes] = useState<any>([]);
  const [allSkinIssueTypesSelected, setAllSkinIssueTypesSelected] =
    useState(false);
  const [selectedInjuryTypes, setSelectedInjuryTypes] = useState<any>([]);
  const [allInjuryTypesSelected, setAllInjuryTypesSelected] = useState(false);
  const [injuryTypes, setInjuryTypes] = useState<string>("");
  const [isClickedList, setIsClickedList] = useState(false);
  const [isClickedStatus, setIsClickedStatus] = useState(false);
  const [isClickedTrend, setIsClickedTrend] = useState(false);
  const [isClickedIncidentRate, setIsClickedIncidentRate] = useState(false);
  const [isClickedRepeat, setIsClickedRepeat] = useState(false);
  const [isClickedWoundAnalysis, setIsClickedWoundAnalysis] = useState(false);
  const [isClickedInjuryReport, setIsClickedInjuryReport] = useState(false);
  const [facilityIds, setFacilityIds] = useState<string>("");
  const { userFacilities } = useStoreWrapper((state) => ({
    userFacilities: state.userFacilities,
  }));

  const [behaviorIncidentTypes, setBehaviorIncidentTypes] = useState<string[]>([]);
  const [selectedBehaviorIncidentTypes, setSelectedBehaviorIncidentTypes] = useState<any>([]);
  const [behaviorIncidentSubTypes, setBehaviorIncidentSubTypes] = useState<string[]>([]);
  const [selectedBehaviorIncidentSubTypes, setSelectedBehaviorIncidentSubTypes] =
    useState<any>([]);
  const [isClickedBehaviorAnalysis, setIsClickedBehaviorAnalysis] =
    useState(false);

  const { getAllIncidentTypes } = useIncident();
  const { data: incidentTypesData } = getAllIncidentTypes;

  const { skinIssueTypeOptions } = useSkinIssueTypes();
  const { injuryTypeOptions } = useInjuryTypes();

  // Compute the facility to pass to location unit API
  // Use facilityIds state if set, otherwise fall back to behaviorFacility from store
  const selectedFacilityForLocationUnit = useMemo(() => {
    if (facilityIds) return facilityIds;
    if (reportType === "behavior-analysis" && behaviorFacility?.[0]?.value) {
      return behaviorFacility[0].value;
    }
    return "";
  }, [facilityIds, behaviorFacility, reportType]);

  const {
    behaviorIncidentTypeOptions,
    behaviorIncidentSubTypeOptions,
  } = useBehaviorAnalysisOptions();

  const incidentTypeAvailableOptions = useMemo(() => {
    const options = mapIncidentTypeOptions(incidentTypesData);
    return options.map(({ label, value }) => ({ label, value }));
  }, [incidentTypesData]);

  const {
    analyticsIncidentList,
    analyticsIncidentTrend,
    analyticsRepeatIncident,
    analyticsStatusReport,
    analyticsWoundAnalysis,
    analyticsBehaviorAnalysis,
    analyticsIncidentRate,
    analyticsInjuryReport,
  } = useAnalytics({
    facilityIds,
    isClickedList,
    isClickedTrend,
    isClickedRepeat,
    isClickedStatus,
    isClickedWoundAnalysis,
    isClickedBehaviorAnalysis,
    isClickedIncidentRate,
    isClickedInjuryReport,
    incidentTypes,
    residentStatus,
    skinIssueTypes,
    injuryTypes,
    behaviorIncidentTypes,
    behaviorIncidentSubTypes,
    startDate: moment(startDate).format("yyyy-MM-DD"),
    endDate: moment(endDate).format("yyyy-MM-DD"),
    grouping,
  });

  const { data: analyticsListData } = analyticsIncidentList;
  const { data: analyticsTrendData } = analyticsIncidentTrend;
  const { data: analyticsRepeatIncidentData } = analyticsRepeatIncident;
  const { data: analyticsStatusReportData } = analyticsStatusReport;
  const { data: analyticsWoundAnalysisData } = analyticsWoundAnalysis;
  const { data: analyticsBehaviorAnalysisData } = analyticsBehaviorAnalysis;
  const { data: analyticsIncidentRateData } = analyticsIncidentRate;
  const { data: analyticsInjuryReportData } = analyticsInjuryReport;

  const incidentListInitialValues = getIncidentListInitialValues();
  const repeatIncidentInitialValues = getRepeatIncidentInitialValues();
  const incidentTrendInitialValues = getIncidentTrendInitialValues();
  const statusReportInitialValues = getStatusReportInitialValues();
  const woundAnalysisInitialValues = getWoundAnalysisInitialValues();
  const behaviorAnalysisInitialValues = getBehaviorAnalysisInitialValues();
  const incidentRateInitialValues = getIncidentRateInitialValues();
  const injuryReportInitialValues = getInjuryReportInitialValues();
  const defaultInitialValues = getIncidentTrendInitialValues();

  const { handleSubmit, values, setFieldValue, isValid, validateForm } =
    useFormik({
      initialValues: {
        ...getInitialValues(),
        startDate: initialStartDate || startDate,
        endDate: initialEndDate || endDate,
      },
      validationSchema: getValidationSchema(),
      validateOnMount: true,
      validateOnChange: true,
      onSubmit: (values: any) => {
        const selectedIncidentLabels = Array.isArray(values.incidentTypes)
          ? values.incidentTypes
          : [];
        const joinedIncidentTypes = selectedIncidentLabels.join(",");

        const selectedSkinIssueLabels = Array.isArray(values.skinIssueTypes)
          ? values.skinIssueTypes
          : [];
        const joinedSkinIssueTypes = selectedSkinIssueLabels.join(",");

        const selectedInjuryTypeLabels = Array.isArray(values.injuryTypes)
          ? values.injuryTypes
          : [];
        const joinedInjuryTypes = selectedInjuryTypeLabels.join(",");

        const selectedBehaviorIncidentTypeLabels = Array.isArray(values.behaviorIncidentTypes)
          ? values.behaviorIncidentTypes
          : [];
        const joinedBehaviorIncidentTypes = selectedBehaviorIncidentTypeLabels.join(",");

        const selectedBehaviorIncidentSubTypeLabels = Array.isArray(values.behaviorIncidentSubTypes)
          ? values.behaviorIncidentSubTypes
          : [];
        const joinedBehaviorIncidentSubTypes = selectedBehaviorIncidentSubTypeLabels.join(",");

        if (isValid) {
          setGlobalAnaylyticsFacility([facilityIds]);

          // Set global selection values first so they are available
          // to the rest of the app when the queries run.
          if (reportType === "incident-list") {
            setGlobalAnalyticsSelectedIncidentType(selectedIncidentLabels);
          }
          if (reportType === "repeat-incident") {
            setGlobalAnalyticsSelectedIncidentType(selectedIncidentLabels);
          }
          if (reportType === "wound-analysis") {
            setGlobalAnalyticsSelectedWoundType(selectedSkinIssueLabels);
            setWoundFacility(selectedFacility || null);
          }
          if (reportType === "behavior-analysis") {
            setBehaviorFacility(selectedFacility || null);
            setGlobalAnalyticsSelectedBehaviorIncidentType(selectedBehaviorIncidentTypeLabels);
            setGlobalAnalyticsSelectedBehaviorIncidentSubType(selectedBehaviorIncidentSubTypeLabels);
          }
          if (reportType === "incident-rate") {
            setGlobalAnalyticsSelectedIncidentType(selectedIncidentLabels);
          }
          if (reportType === "injury-report") {
            setGlobalAnalyticsSelectedIncidentType(selectedIncidentLabels);
            setGlobalAnalyticsSelectedInjuryType(selectedInjuryTypeLabels);
          }
        }

        // Update local state values (these are passed into the query hook)
        setIncidentTypes(joinedIncidentTypes);
        setSkinIssueTypes(joinedSkinIssueTypes);
        setInjuryTypes(joinedInjuryTypes);
        setBehaviorIncidentTypes(joinedBehaviorIncidentTypes);
        setBehaviorIncidentSubTypes(joinedBehaviorIncidentSubTypes);
        setStartDate(moment(values.startDate).format("MM-DD-yyyy"));
        setEndDate(moment(values.endDate).format("MM-DD-yyyy"));

        // Trigger the clicked flags AFTER updating the params so the
        // query-enabled conditions evaluate with the newest values.
        if (isValid) {
          if (reportType === "incident-list") {
            setIsClickedList(true);
          }
          if (reportType === "repeat-incident") {
            setIsClickedRepeat(true);
          }
          if (reportType === "status-report") {
            setIsClickedStatus(true);
          }
          if (reportType === "incident-trend") {
            setGlobalAnalyticsSelectedIncidentType(selectedIncidentLabels);
            setIsClickedTrend(true);
          }
          if (reportType === "wound-analysis") {
            setIsClickedWoundAnalysis(true);
          }
          if (reportType === "behavior-analysis") {
            setIsClickedBehaviorAnalysis(true);
          }
          if (reportType === "incident-rate") {
            setIsClickedIncidentRate(true);
          }
          if (reportType === "injury-report") {
            setIsClickedInjuryReport(true);
          }
        }
        if (bottom) {
          queryClient.invalidateQueries({
            queryKey: [getQueryToInvalidate()],
          });
        }
      },
    });

  useEffect(() => {
    validateForm();
  }, [values]);

  function getQueryToInvalidate() {
    if (reportType === "incident-list") return "incidentList";
    if (reportType === "incident-trend") return "incidentTrend";
    if (reportType === "repeat-incident") return "repeatIncident";
    if (reportType === "status-report") return "statusReport";
    if (reportType === "wound-analysis") return "woundAnalysis";
    if (reportType === "behavior-analysis") return "behaviorAnalysis";
    if (reportType === "incident-rate") return "incidentRate";
    if (reportType === "injury-report") return "injuryReport";
    return "";
  }

  function getInitialValues() {
    if (reportType === "incident-list") return incidentListInitialValues;
    if (reportType === "repeat-incident") return repeatIncidentInitialValues;
    if (reportType === "incident-trend") return incidentTrendInitialValues;
    if (reportType === "status-report") return statusReportInitialValues;
    if (reportType === "wound-analysis") return woundAnalysisInitialValues;
    if (reportType === "behavior-analysis")
      return behaviorAnalysisInitialValues;
    if (reportType === "incident-rate") return incidentRateInitialValues;
    if (reportType === "injury-report") return injuryReportInitialValues;
    return defaultInitialValues;
  }

  function getValidationSchema() {
    if (reportType === "incident-list") return incidentListValidator();
    if (reportType === "repeat-incident") return repeatIncidentValidator();
    if (reportType === "incident-trend") return incidentTrendValidator();
    if (reportType === "status-report") return statusReportValidator();
    if (reportType === "wound-analysis") return woundAnalysisValidator();
    if (reportType === "behavior-analysis") return behaviorAnalysisValidator();
    if (reportType === "incident-rate") return incidentRateValidator();
    if (reportType === "injury-report") return injuryReportValidator();
  }

  function handleFacilityIdsChange(opt: any) {
    if (reportType === "incident-trend" || reportType === "behavior-analysis") {
      const nextSelection = opt ? [opt] : [];
      const nextFacilityId = opt ? opt.value : "";

      setSelectedFacility(nextSelection);
      if (reportType === "incident-trend") {
        setTrendSelectedFacility(opt);
      } else if (reportType === "behavior-analysis") {
        setBehaviorFacility(opt);
      }
      setFieldValue("facilityIds", nextFacilityId);
      setFacilityIds(nextFacilityId);
      setGlobalAnaylyticsFacility(nextFacilityId ? [nextFacilityId] : null);
      return;
    }

    const optionArray = Array.isArray(opt) ? opt : [];

    const hasAllFacilitiesSelected = optionArray.some(
      (option: any) => option.value === "all_facilities"
    );

    if (!optionArray.length) {
      setAllFacilitySelected(false);
      setSelectedFacility([]);
      setFacilityIds("");
      setFieldValue("facilityIds", []);
      setGlobalAnaylyticsFacility(null);
      return;
    }

    let normalizedOptions = optionArray;

    if (hasAllFacilitiesSelected) {
      setAllFacilitySelected(true);
      normalizedOptions = Array.isArray(userFacilities)
        ? userFacilities.map((facility: any) => ({
            label: facility.facilityName,
            value: facility.facilityId,
          }))
        : optionArray.filter(
            (option: any) => option.value !== "all_facilities"
          );
      setSelectedFacility([
        { label: "All Facilities", value: "all_facilities" },
      ]);
    } else {
      setAllFacilitySelected(false);
      setSelectedFacility(optionArray);
    }

    const ids = normalizedOptions.map((val: any) => val.value);
    const joinedIds = ids.join();

    setFacilityIds(joinedIds);
    setFieldValue("facilityIds", ids);
    setGlobalAnaylyticsFacility(joinedIds ? [joinedIds] : null);
  }

  function getValuesForSelect(ids: any, objs: any) {
    if (!ids || !ids[0]) return [];
    const idArray = ids[0].split(",");
    const matched = objs
      .filter((obj: any) => idArray.includes(obj.facilityId))
      .map((obj: any) => ({ value: obj.facilityId, label: obj.facilityName }));

    // deduplicate by facilityId/value
    return Array.from(new Map(matched.map((m: any) => [m.value, m])).values());
  }

  const memoizedValues = useMemo(() => {
    let facilities: any[] = [];
    let facilityIdsJoined = "";
    let selectedFacilityOptions: any[] = [];

    // Handle behavior-analysis single-select facility (similar to incident-trend)
    if (reportType === "behavior-analysis" && behaviorFacility?.length) {
      // behaviorFacility is an array with one SelectOption for single-select
      const facilityOption = behaviorFacility[0];
      if (facilityOption?.value) {
        selectedFacilityOptions = [facilityOption];
        facilityIdsJoined = facilityOption.value;
      }
    } else if (
      globalAnaylyticsFacility?.length &&
      reportType !== "incident-trend" &&
      reportType !== "behavior-analysis"
    ) {
      const ids = globalAnaylyticsFacility.map((val: any) => val);
      facilities = userFacilities
        ? getValuesForSelect(ids, userFacilities)
        : [];

      // compute unique facility count to avoid duplicate count mismatches
      const uniqueUserFacilityCount = Array.isArray(userFacilities)
        ? Array.from(new Set(userFacilities.map((f: any) => f.facilityId)))
            .length
        : 0;

      selectedFacilityOptions =
        facilities.length === uniqueUserFacilityCount
          ? [{ label: "All Facilities", value: "all_facilities" }]
          : facilities;

      facilityIdsJoined = ids.join();
    } else if (globalAnaylyticsFacility?.length) {
      const ids = globalAnaylyticsFacility.map((val: any) => val);
      facilities = userFacilities
        ? getValuesForSelect(ids, userFacilities)
        : [];
      selectedFacilityOptions = facilities;
      facilityIdsJoined = ids.join();
    }

    let incidentTypeLabels: string[] = [];
    let incidentTypeSelectedOptions: any[] = [];
    let preselectAllIncidents = false;

    if (
      (reportType === "incident-list" ||
        reportType === "repeat-incident" ||
        reportType === "incident-rate" ||
        reportType === "injury-report" ||
        reportType === "incident-trend") &&
      globalAnalyticsSelectedIncidentType?.length
    ) {
      const { labels, matchingOptions, isAllTokenSelected, isAllSelected } =
        resolveIncidentTypeSelection(
          globalAnalyticsSelectedIncidentType,
          incidentTypeAvailableOptions
        );

      if (isAllTokenSelected || isAllSelected) {
        preselectAllIncidents = true;
        incidentTypeLabels = incidentTypeAvailableOptions.map(
          (option: any) => option.label
        );
        incidentTypeSelectedOptions = [ALL_INCIDENTS_OPTION];
      } else {
        incidentTypeLabels = labels;
        incidentTypeSelectedOptions = matchingOptions.map((option: any) => ({
          label: option.label,
          value: option.value,
        }));

        // include any labels without matching options to avoid losing custom entries
        const unmatched = labels.filter(
          (label) =>
            !incidentTypeSelectedOptions.some(
              (option: any) => option.label === label
            )
        );
        if (unmatched.length) {
          incidentTypeSelectedOptions = [
            ...incidentTypeSelectedOptions,
            ...unmatched.map((label) => ({ label, value: label })),
          ];
        }
      }
    }

    let residentStatusArray: string[] = [];
    if (
      reportType === "repeat-incident" &&
      globalAnalyticsSelectedResidentStatus?.length
    ) {
      residentStatusArray = globalAnalyticsSelectedResidentStatus;
    }

    if (
      reportType === "incident-rate" &&
      globalAnalyticsSelectedResidentStatus?.length
    ) {
      residentStatusArray = globalAnalyticsSelectedResidentStatus;
    }

    let skinIssueTypeLabels: string[] = [];
    let skinIssueTypeSelectedOptions: any[] = [];

    if (
      reportType === "wound-analysis" &&
      globalAnalyticsSelectedWoundType?.length
    ) {
      skinIssueTypeLabels = globalAnalyticsSelectedWoundType;

      // Get available options excluding the "All" option
      const actualSkinIssueTypes = skinIssueTypeOptions.filter(
        (opt: any) => opt.value !== ALL_SKIN_ISSUE_TYPES_OPTION.value
      );

      // If all actual types are selected, show "All Skin Issue Types" option
      if (skinIssueTypeLabels.length === actualSkinIssueTypes.length) {
        skinIssueTypeSelectedOptions = [ALL_SKIN_ISSUE_TYPES_OPTION];
      } else {
        skinIssueTypeSelectedOptions = globalAnalyticsSelectedWoundType.map(
          (label: string) =>
            skinIssueTypeOptions.find((opt: any) => opt.label === label) || {
              label,
              value: label,
            }
        );
      }
    }

    let behaviorIncidentTypeLabels: string[] = [];
    let behaviorIncidentTypeSelectedOptions: any[] = [];
    let behaviorIncidentSubTypeLabels: string[] = [];
    let behaviorIncidentSubTypeSelectedOptions: any[] = [];

    if (
      reportType === "behavior-analysis" &&
      globalAnalyticsSelectedBehaviorIncidentType?.length
    ) {
      behaviorIncidentTypeLabels = globalAnalyticsSelectedBehaviorIncidentType;

      // Get available options excluding the "All" option
      const actualBehaviorIncidentTypes = behaviorIncidentTypeOptions.filter(
        (opt: any) => opt.value !== ALL_BEHAVIOR_INCIDENT_TYPES_OPTION.value
      );

      // If all actual types are selected, show all individual options (not just "All" chip)
      if (behaviorIncidentTypeLabels.length === actualBehaviorIncidentTypes.length) {
        behaviorIncidentTypeSelectedOptions = actualBehaviorIncidentTypes;
      } else {
        behaviorIncidentTypeSelectedOptions = globalAnalyticsSelectedBehaviorIncidentType.map(
          (label: string) =>
            behaviorIncidentTypeOptions.find((opt: any) => opt.label === label) || {
              label,
              value: label,
            }
        );
      }
    }

    if (
      reportType === "behavior-analysis" &&
      globalAnalyticsSelectedBehaviorIncidentSubType?.length
    ) {
      behaviorIncidentSubTypeLabels = globalAnalyticsSelectedBehaviorIncidentSubType;

      // Get available options excluding the "All" option
      const actualBehaviorIncidentSubTypes = behaviorIncidentSubTypeOptions.filter(
        (opt: any) => opt.value !== ALL_BEHAVIOR_INCIDENT_SUB_TYPES_OPTION.value
      );

      // If all actual types are selected, show all individual options (not just "All" chip)
      if (behaviorIncidentSubTypeLabels.length === actualBehaviorIncidentSubTypes.length) {
        behaviorIncidentSubTypeSelectedOptions = actualBehaviorIncidentSubTypes;
      } else {
        behaviorIncidentSubTypeSelectedOptions = globalAnalyticsSelectedBehaviorIncidentSubType.map(
          (label: string) =>
            behaviorIncidentSubTypeOptions.find((opt: any) => opt.label === label) || {
              label,
              value: label,
            }
        );
      }
    }

    // if (
    //   reportType === "behavior-analysis" &&
    //   globalAnalyticsSelectedTargetOfAggression?.length
    // ) {
    //   targetOfAggressionLabels = globalAnalyticsSelectedTargetOfAggression;

    //   // Get available options excluding the "All" option
    //   const actualTargetTypes = targetOfAggressionOptions.filter(
    //     (opt: any) => opt.value !== ALL_TARGET_OF_AGGRESSION_OPTION.value
    //   );

    //   // If all actual types are selected, show "All Targets of Aggression" option
    //   if (targetOfAggressionLabels.length === actualTargetTypes.length) {
    //     targetOfAggressionSelectedOptions = [ALL_TARGET_OF_AGGRESSION_OPTION];
    //   } else {
    //     targetOfAggressionSelectedOptions =
    //       globalAnalyticsSelectedTargetOfAggression.map(
    //         (label: string) =>
    //           targetOfAggressionOptions.find(
    //             (opt: any) => opt.label === label
    //           ) || {
    //             label,
    //             value: label,
    //           }
    //       );
    //   }
    // }

    // if (
    //   reportType === "behavior-analysis" &&
    //   globalAnalyticsSelectedLocationUnit?.length
    // ) {
    //   locationUnitLabels = globalAnalyticsSelectedLocationUnit;

    //   // Get available options excluding the "All" option
    //   const actualLocationUnits = locationUnitOptions.filter(
    //     (opt: any) => opt.value !== ALL_LOCATION_UNIT_OPTION.value
    //   );

    //   // If all actual types are selected, show "All Location Units" option
    //   if (locationUnitLabels.length === actualLocationUnits.length) {
    //     locationUnitSelectedOptions = [ALL_LOCATION_UNIT_OPTION];
    //   } else {
    //     locationUnitSelectedOptions = globalAnalyticsSelectedLocationUnit.map(
    //       (label: string) =>
    //         locationUnitOptions.find((opt: any) => opt.label === label) || {
    //           label,
    //           value: label,
    //         }
    //     );
    //   }
    // }

    let injuryTypeLabels: string[] = [];
    let injuryTypeSelectedOptions: any[] = [];

    if (
      reportType === "injury-report" &&
      globalAnalyticsSelectedInjuryType?.length
    ) {
      injuryTypeLabels = globalAnalyticsSelectedInjuryType;

      // Get available options excluding the "All" option
      const actualInjuryTypes = injuryTypeOptions.filter(
        (opt: any) => opt.value !== ALL_INJURY_TYPES_OPTION.value
      );

      // If all actual types are selected, show "All Injury Types" option
      if (injuryTypeLabels.length === actualInjuryTypes.length) {
        injuryTypeSelectedOptions = [ALL_INJURY_TYPES_OPTION];
      } else {
        injuryTypeSelectedOptions = globalAnalyticsSelectedInjuryType.map(
          (label: string) =>
            injuryTypeOptions.find((opt: any) => opt.label === label) || {
              label,
              value: label,
            }
        );
      }
    }

    return {
      facilities,
      facilityIdsJoined,
      selectedFacilityOptions,
      incidentTypeLabels,
      incidentTypeSelectedOptions,
      preselectAllIncidents,
      residentStatusArray,
      skinIssueTypeLabels,
      skinIssueTypeSelectedOptions,
      behaviorIncidentTypeLabels,
      behaviorIncidentTypeSelectedOptions,
      behaviorIncidentSubTypeLabels,
      behaviorIncidentSubTypeSelectedOptions,
      injuryTypeLabels,
      injuryTypeSelectedOptions,
    };
  }, [
    globalAnaylyticsFacility,
    globalAnalyticsSelectedIncidentType,
    globalAnalyticsSelectedResidentStatus,
    globalAnalyticsSelectedWoundType,
    globalAnalyticsSelectedInjuryType,
    globalAnalyticsSelectedBehaviorIncidentType,
    globalAnalyticsSelectedBehaviorIncidentSubType,
    incidentTypeAvailableOptions,
    skinIssueTypeOptions,
    injuryTypeOptions,
    reportType,
    userFacilities,
    behaviorIncidentTypeOptions,
    behaviorIncidentSubTypeOptions,
    behaviorFacility,
  ]);

  /** CRITICAL DEVELOPER NOTE:
   * The following useEffect is responsible for resetting ALL form values when the report type changes.
   * DO NOT remove any state resets from this useEffect.
   * This is necessary to prevent values from one report type "bleeding" into another when switching between them, which can cause incorrect query results and a confusing user experience.
   * If you need to add new form values or state variables, make sure to include them in this reset logic if they should be cleared when switching report types.
   * Always test switching between all report types after making changes to ensure that values are being reset appropriately and that there is no unintended persistence of state across different report types.
   * If you find that certain values should persist across report types, carefully manage that logic to ensure it only persists where appropriate and does not cause confusion or incorrect data in other report types.
   * In summary: this useEffect is the "reset everything" switch when changing report types, and it is crucial for maintaining the integrity of the form state as users navigate between different analytics reports. Handle with care!
   **/
  useEffect(() => {
    // If in modify mode, do not reset
    if (isModifyMode) return;
    // Facility
    setSelectedFacility([]);
    setFacilityIds("");
    setGlobalAnaylyticsFacility(null);
    setTrendSelectedFacility(null);
    setWoundFacility(null);
    setBehaviorFacility(null);

    // Incident Types
    setSelectedIncidentTypes([]);
    setIncidentTypes("");
    setGlobalAnalyticsSelectedIncidentType([]);

    // Dates
    setStartDate("");
    setEndDate("");

    // Resident Status
    setResidentStatus([]);
    setGlobalAnalyticsSelectedResidentStatus([]);

    // Skin Issue Types
    setSkinIssueTypes([]);
    setSelectedSkinIssueTypes([]);
    setAllSkinIssueTypesSelected(false);
    setGlobalAnalyticsSelectedWoundType([]);

    // Injury Types
    setInjuryTypes("");
    setSelectedInjuryTypes([]);
    setAllInjuryTypesSelected(false);
    setGlobalAnalyticsSelectedInjuryType([]);

    // Behavior Incident Types
    setBehaviorIncidentTypes([]);
    setSelectedBehaviorIncidentTypes([]);
    setGlobalAnalyticsSelectedBehaviorIncidentType([]);

    // Behavior Incident Sub Types
    setBehaviorIncidentSubTypes([]);
    setSelectedBehaviorIncidentSubTypes([]);
    setGlobalAnalyticsSelectedBehaviorIncidentSubType([]);

    // Grouping
    setGrouping("Monthly");

    // Clicked flags
    setIsClickedList(false);
    setIsClickedStatus(false);
    setIsClickedTrend(false);
    setIsClickedIncidentRate(false);
    setIsClickedRepeat(false);
    setIsClickedWoundAnalysis(false);
    setIsClickedInjuryReport(false);
    setIsClickedBehaviorAnalysis(false);

    // Formik values
    setFieldValue("facilityIds", []);
    setFieldValue("incidentTypes", []);
    setFieldValue("startDate", "");
    setFieldValue("endDate", "");
    setFieldValue("residentStatus", []);
    setFieldValue("skinIssueTypes", []);
    setFieldValue("injuryTypes", []);
    setFieldValue("behaviorIncidentTypes", []);
    setFieldValue("behaviorIncidentSubTypes", []);
  }, [reportType, isModifyMode]);

  useEffect(() => {
    const {
      selectedFacilityOptions,
      facilityIdsJoined,
      incidentTypeLabels,
      incidentTypeSelectedOptions,
      preselectAllIncidents,
      residentStatusArray,
      skinIssueTypeLabels,
      skinIssueTypeSelectedOptions,
      injuryTypeLabels,
      injuryTypeSelectedOptions,
      behaviorIncidentTypeLabels,
      behaviorIncidentTypeSelectedOptions,
      behaviorIncidentSubTypeLabels,
      behaviorIncidentSubTypeSelectedOptions,
    } = memoizedValues;

    setSelectedFacility(selectedFacilityOptions);
    setFacilityIds(facilityIdsJoined);
    setFieldValue(
      "facilityIds",
      reportType === "incident-trend" || reportType === "behavior-analysis"
        ? facilityIdsJoined
        : facilityIdsJoined
          ? facilityIdsJoined.split(",")
          : []
    );

    if (
      reportType === "incident-list" ||
      reportType === "repeat-incident" ||
      reportType === "incident-rate" ||
      reportType === "injury-report" ||
      reportType === "incident-trend"
    ) {
      if (preselectAllIncidents) {
        setSelectedIncidentTypes([ALL_INCIDENTS_OPTION]);
        setAllIncidentsSelected(true);
        const labels = incidentTypeAvailableOptions.map(
          (option: any) => option.label
        );
        setFieldValue("incidentTypes", labels);
        setIncidentTypes(labels.join(","));
      } else if (incidentTypeLabels.length) {
        setSelectedIncidentTypes(incidentTypeSelectedOptions);
        setAllIncidentsSelected(false);
        setFieldValue("incidentTypes", incidentTypeLabels);
        setIncidentTypes(incidentTypeLabels.join(","));
      } else {
        setSelectedIncidentTypes([]);
        setAllIncidentsSelected(false);
        setFieldValue("incidentTypes", []);
        setIncidentTypes("");
      }
    }

    // Restore resident status for reports that use it
    if (reportType === "repeat-incident" || reportType === "incident-rate") {
      if (residentStatusArray.length) {
        setFieldValue("residentStatus", residentStatusArray);
        setResidentStatus(residentStatusArray);
      } else {
        setFieldValue("residentStatus", []);
        setResidentStatus([]);
      }
    }

    // Restore skin issue types for wound analysis reports
    if (reportType === "wound-analysis") {
      if (skinIssueTypeLabels.length) {
        setSelectedSkinIssueTypes(skinIssueTypeSelectedOptions);
        setFieldValue("skinIssueTypes", skinIssueTypeLabels);
        setSkinIssueTypes(skinIssueTypeLabels);
      } else {
        setSelectedSkinIssueTypes([]);
        setFieldValue("skinIssueTypes", []);
        setSkinIssueTypes([]);
      }
    }

    if (reportType === "behavior-analysis") {
      if (behaviorIncidentTypeLabels.length) {
        setSelectedBehaviorIncidentTypes(behaviorIncidentTypeSelectedOptions);
        setFieldValue("behaviorIncidentTypes", behaviorIncidentTypeLabels);
        setBehaviorIncidentTypes(behaviorIncidentTypeLabels);
      } else {
        setSelectedBehaviorIncidentTypes([]);
        setFieldValue("behaviorIncidentTypes", []);
        setBehaviorIncidentTypes([]);
      }

      if (behaviorIncidentSubTypeLabels.length) {
        setSelectedBehaviorIncidentSubTypes(behaviorIncidentSubTypeSelectedOptions);
        setFieldValue("behaviorIncidentSubTypes", behaviorIncidentSubTypeLabels);
        setBehaviorIncidentSubTypes(behaviorIncidentSubTypeLabels);
      } else {
        setSelectedBehaviorIncidentSubTypes([]);
        setFieldValue("behaviorIncidentSubTypes", []);
        setBehaviorIncidentSubTypes([]);
      }
    }

    // Restore injury types for injury reports
    if (reportType === "injury-report") {
      if (injuryTypeLabels.length) {
        setSelectedInjuryTypes(injuryTypeSelectedOptions);
        setFieldValue("injuryTypes", injuryTypeLabels);
        setInjuryTypes(injuryTypeLabels.join(","));
      } else {
        setSelectedInjuryTypes([]);
        setFieldValue("injuryTypes", []);
        setInjuryTypes("");
      }
    }
  }, [incidentTypeAvailableOptions, memoizedValues, reportType, setFieldValue]);

  function handleIncidentTypeChange(opt: any) {
    if (
      reportType === "incident-list" ||
      reportType === "repeat-incident" ||
      reportType === "incident-rate" ||
      reportType === "injury-report" ||
      reportType === "incident-trend"
    ) {
      if (
        opt.some((option: any) => option.value === ALL_INCIDENTS_OPTION.value)
      ) {
        setSelectedIncidentTypes([ALL_INCIDENTS_OPTION]);
        setAllIncidentsSelected(true);
        const allIncidentTypes = incidentTypeAvailableOptions.map(
          (incident: any) => incident.label
        );
        setIncidentTypes(allIncidentTypes.join(","));
        setFieldValue("incidentTypes", allIncidentTypes);
        setGlobalAnalyticsSelectedIncidentType([ALL_INCIDENTS_OPTION.label]);
      } else {
        setAllIncidentsSelected(false);
        setSelectedIncidentTypes(opt);
        const types = opt.map((val: any) => val.label);
        setIncidentTypes(types.join(","));
        setFieldValue("incidentTypes", types);
        setGlobalAnalyticsSelectedIncidentType(types);
      }
    } else {
      setFieldValue("incidentType", opt.label);
      setIncidentTypes(opt.label);
      setGlobalAnalyticsSelectedIncidentType([opt.label]);
    }
  }

  function handleSkinIssueTypeChange(opt: any) {
    const optionArray = Array.isArray(opt) ? opt : [];

    // Check if "All Skin Issue Types" was selected
    if (
      optionArray.some(
        (option: any) => option.value === ALL_SKIN_ISSUE_TYPES_OPTION.value
      )
    ) {
      setSelectedSkinIssueTypes([ALL_SKIN_ISSUE_TYPES_OPTION]);
      setAllSkinIssueTypesSelected(true);
      // Get all skin issue types except the "All" option
      const allSkinTypes = skinIssueTypeOptions
        .filter(
          (option: any) => option.value !== ALL_SKIN_ISSUE_TYPES_OPTION.value
        )
        .map((option: any) => option.label);
      setSkinIssueTypes(allSkinTypes);
      setFieldValue("skinIssueTypes", allSkinTypes);
    } else {
      setAllSkinIssueTypesSelected(false);
      setSelectedSkinIssueTypes(optionArray);
      const types = optionArray.map((val: any) => val.label);
      setSkinIssueTypes(types);
      setFieldValue("skinIssueTypes", types);
    }
  }

  function handleInjuryTypeChange(opt: any) {
    const optionArray = Array.isArray(opt) ? opt : [];

    // Check if "All Injury Types" was selected
    if (
      optionArray.some(
        (option: any) => option.value === ALL_INJURY_TYPES_OPTION.value
      )
    ) {
      setSelectedInjuryTypes([ALL_INJURY_TYPES_OPTION]);
      setAllInjuryTypesSelected(true);
      // Get all injury types except the "All" option
      const allInjuryTypesList = injuryTypeOptions
        .filter((option: any) => option.value !== ALL_INJURY_TYPES_OPTION.value)
        .map((option: any) => option.label);
      setInjuryTypes(allInjuryTypesList.join(","));
      setFieldValue("injuryTypes", allInjuryTypesList);
    } else {
      setAllInjuryTypesSelected(false);
      setSelectedInjuryTypes(optionArray);
      const types = optionArray.map((val: any) => val.label);
      setInjuryTypes(types.join(","));
      setFieldValue("injuryTypes", types);
    }
  }

  function handleBehaviorIncidentTypeChange(opt: any) {
    const optionArray = Array.isArray(opt) ? opt : [];

    // Check if "All Behavior Issue Types" was selected
    if (
      optionArray.some(
        (option: any) => option.value === ALL_BEHAVIOR_INCIDENT_TYPES_OPTION.value
      )
    ) {
      // Get all behavior types except the "All" option
      const allBehaviorTypesList = behaviorIncidentTypeOptions
        .filter(
          (option: any) => option.value !== ALL_BEHAVIOR_INCIDENT_TYPES_OPTION.value
        );
      
      // Show all individual options in the dropdown, not just "All"
      setSelectedBehaviorIncidentTypes(allBehaviorTypesList);
      
      // Extract labels for the actual values
      const allLabels = allBehaviorTypesList.map((option: any) => option.label);
      setBehaviorIncidentTypes(allLabels);
      setFieldValue("behaviorIncidentTypes", allLabels);
      setGlobalAnalyticsSelectedBehaviorIncidentType(allLabels);
    } else {
      setSelectedBehaviorIncidentTypes(optionArray);
      const types = optionArray.map((val: any) => val.label);
      setBehaviorIncidentTypes(types);
      setFieldValue("behaviorIncidentTypes", types);
      setGlobalAnalyticsSelectedBehaviorIncidentType(types);
    }
  }

  function handleBehaviorIncidentSubTypeChange(opt: any) {
    const optionArray = Array.isArray(opt) ? opt : [];

    console.log("handleBehaviorIncidentSubTypeChange called with:", opt);
    console.log("behaviorIncidentSubTypeOptions:", behaviorIncidentSubTypeOptions);

    // Check if "All Behavior Incident Sub Types" was selected
    if (
      optionArray.some(
        (option: any) => option.value === ALL_BEHAVIOR_INCIDENT_SUB_TYPES_OPTION.value
      )
    ) {
      // Get all sub types except the "All" option
      const allSubTypesList = behaviorIncidentSubTypeOptions
        .filter(
          (option: any) =>
            option.value !== ALL_BEHAVIOR_INCIDENT_SUB_TYPES_OPTION.value
        );
      
      console.log("All selected, showing all options:", allSubTypesList);
      
      // Show all individual options in the dropdown, not just "All"
      setSelectedBehaviorIncidentSubTypes(allSubTypesList);
      
      // Extract labels for the actual values
      const allLabels = allSubTypesList.map((option: any) => option.label);
      setBehaviorIncidentSubTypes(allLabels);
      setFieldValue("behaviorIncidentSubTypes", allLabels);
      setGlobalAnalyticsSelectedBehaviorIncidentSubType(allLabels);
    } else {
      setSelectedBehaviorIncidentSubTypes(optionArray);
      const types = optionArray.map((val: any) => val.label);
      
      console.log("Individual selection, setting values to:", types);
      
      setBehaviorIncidentSubTypes(types);
      setFieldValue("behaviorIncidentSubTypes", types);
      setGlobalAnalyticsSelectedBehaviorIncidentSubType(types);
    }
  }

  useEffect(() => {
    if (Array.isArray(userFacilities)) {
      // Normalize and deduplicate user facilities by facilityId
      const transformed = Array.from(
        new Map(
          userFacilities.map((facility: any) => [
            facility.facilityId,
            {
              label: facility.facilityName,
              value: facility.facilityId,
            },
          ])
        ).values()
      );

      let allFacilities: any = [];
      if (
        reportType === "incident-trend" ||
        reportType === "behavior-analysis"
      ) {
        allFacilities = transformed;
      } else if (allFacilitySelected) {
        // Show only the All Facilities chip when all is selected
        allFacilities = [{ label: "All Facilities", value: "all_facilities" }];
      } else {
        allFacilities = [
          { label: "All Facilities", value: "all_facilities" },
          ...transformed,
        ];
      }
      setFacilityOptions(allFacilities);
    }
  }, [userFacilities, allFacilitySelected, reportType]);

  useEffect(() => {
    const allIncidents = allIncidentsSelected
      ? incidentTypeAvailableOptions
      : [ALL_INCIDENTS_OPTION, ...incidentTypeAvailableOptions];

    setIncidentListOptions(allIncidents);
  }, [allIncidentsSelected, incidentTypeAvailableOptions]);

  useEffect(() => {
    if (isClickedList && analyticsListData?.data?.data) {
      setIsClickedList(false);
      const { data } = analyticsListData.data;
      setAnalyticsIncidentList(data);
      bottom ? closeFn() : submitFn && submitFn();
    }
  }, [analyticsListData, isClickedList]);

  useEffect(() => {
    if (isClickedStatus && analyticsStatusReportData?.data?.data) {
      setIsClickedStatus(false);
      const { data } = analyticsStatusReportData.data;
      setAnalyticsStatusReport(data);
      bottom ? closeFn() : submitFn && submitFn();
    }
  }, [analyticsStatusReportData, isClickedStatus]);

  useEffect(() => {
    if (isClickedRepeat && analyticsRepeatIncidentData?.data?.data) {
      setIsClickedRepeat(false);
      const { data } = analyticsRepeatIncidentData.data;
      setAnalyticsRepeatIncident(data);
      bottom ? closeFn() : submitFn && submitFn();
    }
  }, [analyticsRepeatIncidentData, isClickedRepeat]);

  useEffect(() => {
    if (isClickedTrend && analyticsTrendData?.data?.data) {
      setIsClickedTrend(false);
      const { data } = analyticsTrendData.data;
      setAnalyticsIncidentTrend(data);
      bottom ? closeFn() : submitFn && submitFn();
    }
  }, [analyticsTrendData, isClickedTrend]);

  useEffect(() => {
    // Prevent writing cached 'previous' data when a refetch is in-flight.
    // Only update the analytics store when the query has finished fetching
    // and we have a fresh response. This ensures the report grid doesn't
    // get set to the prior result (one-iteration behind) when
    // `keepPreviousData` is enabled on the query.
    if (
      isClickedIncidentRate &&
      !analyticsIncidentRate.isFetching &&
      analyticsIncidentRateData?.data?.data
    ) {
      setIsClickedIncidentRate(false);
      const { data } = analyticsIncidentRateData.data;
      setAnalyticsIncidentRate(data);
      bottom ? closeFn() : submitFn && submitFn();
    }
  }, [
    analyticsIncidentRateData,
    analyticsIncidentRate?.isFetching,
    isClickedIncidentRate,
  ]);

  useEffect(() => {
    if (isClickedWoundAnalysis && analyticsWoundAnalysisData?.data?.data) {
      setIsClickedWoundAnalysis(false);
      const { data } = analyticsWoundAnalysisData.data;
      setWoundAnalysisIncidents(data);
      bottom ? closeFn() : submitFn && submitFn();
    }
  }, [analyticsWoundAnalysisData, isClickedWoundAnalysis]);

  useEffect(() => {
    if (
      isClickedBehaviorAnalysis &&
      analyticsBehaviorAnalysisData?.data?.data
    ) {
      setIsClickedBehaviorAnalysis(false);
      const { data } = analyticsBehaviorAnalysisData.data;
      setBehaviorAnalysisIncidents(data);
      bottom ? closeFn() : submitFn && submitFn();
    }
  }, [analyticsBehaviorAnalysisData, isClickedBehaviorAnalysis]);

  useEffect(() => {
    if (isClickedInjuryReport && analyticsInjuryReportData?.data?.data) {
      setIsClickedInjuryReport(false);
      const { data } = analyticsInjuryReportData.data;
      setAnalyticsInjuryReport(data);
      bottom ? closeFn() : submitFn && submitFn();
    }
  }, [analyticsInjuryReportData, isClickedInjuryReport]);

  function getBtnTxt() {
    if (
      isClickedList ||
      isClickedTrend ||
      isClickedRepeat ||
      isClickedStatus ||
      isClickedWoundAnalysis ||
      isClickedBehaviorAnalysis ||
      isClickedIncidentRate ||
      isClickedInjuryReport
    ) {
      return "Loading...";
    } else {
      return "Generate";
    }
  }

  useEffect(() => {
    // Log selectedFacility whenever it changes
  }, [selectedFacility]);

  return (
    <form onSubmit={handleSubmit} name="on-screen-analytics">
      <div className="mb-7">
        <Label>Select Facility</Label>
        <div data-testid="facility-dropdown">
          <Select
            name="facilityId"
            options={facilityOptions}
            className="basic-multi-select"
            classNamePrefix="select"
            styles={customStyles}
            placeholder="Select Facility"
            isMulti={
              reportType !== "incident-trend" &&
              reportType !== "behavior-analysis"
            }
            onChange={(opt) => {
              handleFacilityIdsChange(opt);
            }}
            value={selectedFacility}
          />
        </div>
      </div>

      {(reportType === "incident-list" ||
        reportType === "repeat-incident" ||
        reportType === "incident-rate" ||
        reportType === "injury-report" ||
        reportType === "incident-trend") && (
        <IncidentTypeSelect
          name="incidentTypes"
          options={incidentListOptions}
          placeholder="Incident Type"
          isMulti={true}
          onChange={(opt) => handleIncidentTypeChange(opt)}
          selected={selectedIncidentTypes}
        />
      )}

      {reportType === "incident-trend" && (
        <div className="mb-7">
          <Label>Period Grouping</Label>
          <Select
            name="grouping"
            options={[
              { label: "Weekly", value: "Weekly" },
              { label: "Monthly", value: "Monthly" },
              { label: "Quarterly", value: "Quarterly" },
              { label: "Yearly", value: "Yearly" },
            ]}
            className="basic-single"
            classNamePrefix="select"
            styles={customStyles}
            placeholder="Select Period"
            isMulti={false}
            onChange={(opt: any) => {
              setGrouping(opt.value);
            }}
            value={{ label: grouping, value: grouping }}
          />
        </div>
      )}

      {(reportType === "repeat-incident" || reportType === "incident-rate") && (
        <div className="mb-7">
          <Label>Resident Status</Label>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="residentStatus"
                value="Current"
                checked={values.residentStatus?.includes("Current")}
                onChange={(e) => {
                  const currentStatuses = values.residentStatus || [];
                  let newStatuses = e.target.checked
                    ? [...currentStatuses, "Current"]
                    : currentStatuses.filter(
                        (status: string) => status !== "Current"
                      );
                  setFieldValue("residentStatus", newStatuses);
                  setResidentStatus(newStatuses);
                  setGlobalAnalyticsSelectedResidentStatus(newStatuses);
                }}
              />
              <span>Current Residents</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="residentStatus"
                value="Discharged"
                checked={values.residentStatus?.includes("Discharged")}
                onChange={(e) => {
                  const currentStatuses = values.residentStatus || [];
                  let newStatuses = e.target.checked
                    ? [...currentStatuses, "Discharged"]
                    : currentStatuses.filter(
                        (status: string) => status !== "Discharged"
                      );
                  setFieldValue("residentStatus", newStatuses);
                  setResidentStatus(newStatuses);
                  setGlobalAnalyticsSelectedResidentStatus(newStatuses);
                }}
              />
              <span>Discharged Residents</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="residentStatusBoth"
                checked={
                  values.residentStatus?.includes("Current") &&
                  values.residentStatus?.includes("Discharged")
                }
                onChange={(e) => {
                  const newStatuses = e.target.checked
                    ? ["Current", "Discharged"]
                    : [];
                  setFieldValue("residentStatus", newStatuses);
                  setResidentStatus(newStatuses);
                  setGlobalAnalyticsSelectedResidentStatus(newStatuses);
                }}
              />
              <span>Current and Discharged Residents</span>
            </label>
          </div>
        </div>
      )}

      {reportType === "wound-analysis" && (
        <div className="mb-7">
          <Label>Skin Issue Type</Label>
          <div data-testid="skin-issue-type-dropdown">
            <Select
              name="skinIssueTypes"
              options={skinIssueTypeOptions}
              className="basic-multi-select"
              classNamePrefix="select"
              styles={customStyles}
              placeholder="Select Skin Issue Type"
              isMulti={true}
              onChange={(opt) => handleSkinIssueTypeChange(opt)}
              value={selectedSkinIssueTypes}
            />
          </div>
        </div>
      )}

      {reportType === "behavior-analysis" && (
        <>
          <div className="mb-7">
            <Label>Behavior Incident Type</Label>
            <div data-testid="behavior-incident-type-dropdown">
              <Select
                name="behaviorIncidentTypes"
                options={behaviorIncidentTypeOptions}
                className="basic-multi-select"
                classNamePrefix="select"
                styles={customStyles}
                placeholder="Select Behavior Incident Type"
                isMulti={true}
                onChange={(opt) => handleBehaviorIncidentTypeChange(opt)}
                value={selectedBehaviorIncidentTypes}
              />
            </div>
          </div>

          <div className="mb-7">
            <Label>Behavior Incident Sub Type</Label>
            <div data-testid="behavior-incident-sub-type-dropdown">
              <Select
                name="behaviorIncidentSubTypes"
                options={behaviorIncidentSubTypeOptions}
                className="basic-multi-select"
                classNamePrefix="select"
                styles={customStyles}
                placeholder="Select Behavior Incident Sub Type"
                isMulti={true}
                onChange={(opt) => handleBehaviorIncidentSubTypeChange(opt)}
                value={selectedBehaviorIncidentSubTypes}
              />
            </div>
          </div>
        </>
      )}

      {reportType === "injury-report" && (
        <div className="mb-7">
          <Label>Injury Type</Label>
          <div data-testid="injury-type-dropdown">
            <Select
              name="injuryTypes"
              options={injuryTypeOptions}
              className="basic-multi-select"
              classNamePrefix="select"
              styles={customStyles}
              placeholder="Select Injury Type"
              isMulti={true}
              onChange={(opt) => handleInjuryTypeChange(opt)}
              value={selectedInjuryTypes}
            />
          </div>
        </div>
      )}

      <DateRangePicker
        startDate={values.startDate}
        endDate={values.endDate}
        setFieldValue={setFieldValue}
      />

      <ActionContainer>
        <ActionBtnContainer
          className="flex w-1/3"
          data-testid="generate-btn-container"
        >
          <ActionBtnU purple_border onClick={() => closeFn()} type="button">
            Cancel
          </ActionBtnU>
          <ActionBtnU
            purpleBg
            white_color
            type="submit"
            disabled={
              !isValid ||
              isClickedList ||
              isClickedRepeat ||
              isClickedTrend ||
              isClickedStatus ||
              isClickedWoundAnalysis ||
              isClickedBehaviorAnalysis ||
              isClickedIncidentRate ||
              isClickedInjuryReport
            }
          >
            {getBtnTxt()}
          </ActionBtnU>
        </ActionBtnContainer>
      </ActionContainer>
    </form>
  );
};

export default GeneralForm;
